import { fold, validName } from './names'
import { identifier, indexExpression, keyword, mentions, ownForeignKey, tableConstraint, tableDefinition, tokenize, type Token } from './lexer'

export type RefusalCode = 'E_APP_TABLE' | 'E_SYSTEM_TABLE' | 'E_PRAGMA' | 'E_ATTACH' | 'E_TX_CONTROL' |
  'E_UNIQUE_INDEX' | 'E_INDEX_KIND' | 'E_ENUM_ARRAY_INDEX' | 'E_TABLE_KIND' | 'E_FTS_COLUMN' |
  'E_NONDETERMINISTIC' | 'E_DEPENDENT_OBJECT' | 'E_MIXED_DATABASES' | 'E_MIXED_PAGE_DDL' |
  'E_REBUILD_FK_ENABLED' | 'E_ID_COLUMN' | 'E_REFERENCED_TABLE' | 'E_NAME' | 'E_NAME_COLLISION' | 'E_SQL'
export interface Refusal { code: RefusalCode; message: string }
export class CustomSqlError extends Error {
  constructor(readonly code: RefusalCode, message: string) { super(`${code}: ${message}`); this.name = 'CustomSqlError' }
}
export interface SchemaRow { type: string; name: string; tbl_name: string; sql: string | null }
export interface ColumnRow { name: string; type: string; hidden: number; pk?: number }
export interface ClassifyContext {
  appTables?: ReadonlySet<string> | readonly string[]
  schemaRows?: readonly SchemaRow[]
  columns?: readonly ColumnRow[]
  enumArrayColumns?: readonly string[]
  ftsColumns?: readonly string[]
  foreignKeysEnabled?: boolean
  referenced?: boolean
  dropReferences?: boolean
  transactionTables?: readonly string[]
  pageId?: string
  pageRowsOnly?: boolean
  mixedPageDdl?: boolean
  typeTables?: readonly string[]
}
export interface Kind {
  type: 'read' | 'dml' | 'table' | 'index' | 'type'
  action: string
  sql: string
  table?: string
  name?: string
  column?: string
  newName?: string
  declaration?: string
  expression?: string
  pageCoupled?: boolean
  rebuild?: boolean
}

/** Explicit deterministic set: unknown/extension functions are refused, never implicitly trusted. */
export const DETERMINISTIC_FUNCTIONS = new Set(`abs acos acosh asin asinh atan atan2 atanh ceil ceiling cos cosh degrees exp floor ln log log10 log2 mod pi pow power radians sin sinh sqrt tan tanh trunc
  coalesce ifnull iif nullif min max sign round length octet_length char unicode unistr unistr_quote upper lower trim ltrim rtrim substr substring replace instr concat concat_ws format printf quote typeof hex unhex zeroblob soundex like glob likely unlikely likelihood
  json jsonb json_array jsonb_array json_object jsonb_object json_extract jsonb_extract json_insert jsonb_insert json_replace jsonb_replace json_set jsonb_set json_remove jsonb_remove json_patch jsonb_patch json_type json_valid json_quote json_array_length
  array array_element array_set_element array_length array_append array_prepend array_cat array_remove array_trim_elements array_contains array_position array_slice string_to_array array_to_string array_overlap array_contains_all
  boolean_to_int int_to_boolean numeric_encode numeric_decode numeric_add numeric_sub numeric_mul numeric_div numeric_lt numeric_eq gcd lcm repeat lpad rpad`.split(/\s+/u))
const dateFunctions = new Set(['date', 'time', 'datetime', 'julianday', 'unixepoch', 'strftime', 'timediff'])
// SQL expression keywords introduce operands/groups, never function calls.
const expressionSyntax = new Set(`case when then else end and or not is isnull notnull in between like glob match regexp escape cast as exists over filter where from partition by order asc desc nulls first last collate distinct all true false null rows range groups unbounded preceding following current row exclude ties`.split(/\s+/u))

export function deterministicExpression(expression: string): boolean {
  const tokens = tokenize(expression)
  for (const [i, token] of tokens.entries()) {
    if (token.type === 'word' && ['CURRENT_TIME', 'CURRENT_DATE', 'CURRENT_TIMESTAMP'].includes(token.value.toUpperCase())) return false
    if (!(identifier(token) || token.type === 'string') || tokens[i + 1]?.raw !== '(') continue
    const fn = token.value.toLowerCase()
    if (token.type === 'word' && expressionSyntax.has(fn)) continue
    // AS introduces CAST's type declaration, including VARCHAR(20) / DECIMAL(p,s).
    // Its parentheses are type parameters, not a call in the value expression.
    const enclosing = tokens.slice(0, i).findLastIndex(prior => prior.raw === '(' && prior.depth === token.depth - 1)
    if (keyword(tokens[enclosing - 1], 'CAST') && tokens.slice(enclosing + 1, i).some(prior => keyword(prior, 'AS') && prior.depth === token.depth)) continue
    if (dateFunctions.has(fn)) {
      const args: Token[] = []
      for (let j = i + 2; j < tokens.length && tokens[j].depth > token.depth; j++) args.push(tokens[j])
      // Column values are allowed; implicit clock inputs and explicit clock modifiers are not.
      if (args.length === 0 || args.some(arg => arg.type === 'parameter')) return false
      if (args.some(arg => arg.type === 'string' && /^(now|localtime|utc)$/iu.test(arg.value))) return false
    } else if (!DETERMINISTIC_FUNCTIONS.has(fn)) return false
  }
  return true
}

function refusal(code: RefusalCode, message: string): Refusal { return { code, message } }
function reserved(name: string, context: ClassifyContext): boolean {
  const n = fold(name)
  return /^(sqlite_|_turso_|turso_|__turso_|__cdb_)/u.test(n) || [...(context.appTables ?? [])].some(table => fold(table) === n)
}
export function isCustomTable(name: string, appTables: ReadonlySet<string> | readonly string[]): boolean {
  return !reserved(name, { appTables })
}
function readName(tokens: Token[], offset: number): { name: string; next: number; database?: string } | undefined {
  if (!identifier(tokens[offset]) && tokens[offset]?.type !== 'string') return undefined
  if (tokens[offset + 1]?.raw === '.' && (identifier(tokens[offset + 2]) || tokens[offset + 2]?.type === 'string')) {
    return { database: tokens[offset].value, name: tokens[offset + 2].value, next: offset + 3 }
  }
  return { name: tokens[offset].value, next: offset + 1 }
}
function skipExists(tokens: Token[], offset: number): number {
  if (keyword(tokens[offset], 'IF')) return offset + (keyword(tokens[offset + 1], 'NOT') ? 3 : 2)
  return offset
}
function nameRefusal(name: string, existing: readonly string[], excluding?: string): Refusal | undefined {
  if (!validName(name)) return refusal('E_NAME', `Invalid identifier ${JSON.stringify(name)}`)
  if (existing.some(other => other !== excluding && fold(other) === fold(name))) return refusal('E_NAME_COLLISION', `Identifier collides with ${name}`)
  return undefined
}

export function classify(sql: string, context: ClassifyContext = {}): Kind | Refusal {
  let tokens: Token[]
  try { tokens = tokenize(sql) } catch (error) { return refusal('E_SQL', String(error)) }
  if (tokens.at(-1)?.type === 'symbol' && tokens.at(-1)?.value === ';') tokens = tokens.slice(0, -1)
  if (tokens.length === 0 || tokens.some(token => token.type === 'symbol' && token.value === ';')) return refusal('E_SQL', 'Expected one statement')
  if (tokens.some((token, i) => (identifier(token) || (token.type === 'string' && ['FROM', 'JOIN', 'INTO', 'UPDATE', 'TABLE', 'INDEX', 'TYPE', 'DOMAIN', 'REFERENCES'].some(word => keyword(tokens[i - 1], word)))) && fold(token.value).startsWith('__cdb_'))) return refusal('E_SYSTEM_TABLE', 'Custom database system objects are private')
  const first = tokens[0].value.toUpperCase()
  if (['ATTACH', 'DETACH'].includes(first)) return refusal('E_ATTACH', 'Attached databases are not allowed')
  if (['BEGIN', 'COMMIT', 'END', 'ROLLBACK', 'SAVEPOINT', 'RELEASE'].includes(first)) return refusal('E_TX_CONTROL', 'Use the driver transaction API')
  if (first === 'PRAGMA') {
    const pragma = readName(tokens, 1)
    if (!pragma) return refusal('E_PRAGMA', 'Invalid pragma')
    const name = pragma.name.toLowerCase()
    if (name.startsWith('capture_') || name.includes('capture_data_changes')) return refusal('E_PRAGMA', 'Capture is driver-owned')
    if (tokens[pragma.next]?.raw === '(' && fold(tokens[pragma.next + 1]?.value ?? '').startsWith('__cdb_')) return refusal('E_SYSTEM_TABLE', 'Custom database system objects are private')
    const readArguments = new Set(['table_info', 'table_xinfo', 'foreign_key_list', 'index_info', 'index_xinfo', 'index_list', 'integrity_check', 'quick_check'])
    const reads = new Set([...readArguments, 'foreign_keys', 'schema_version', 'user_version', 'database_list', 'compile_options', 'encoding', 'journal_mode', 'page_count', 'freelist_count', 'table_list', 'query_only', 'application_id', 'data_version', 'page_size', 'cache_size', 'cache_spill', 'auto_vacuum', 'busy_timeout', 'locking_mode', 'synchronous', 'temp_store', 'mmap_size', 'function_list', 'pragma_list', 'collation_list', 'foreign_key_check'])
    if (!reads.has(name) || tokens.some(token => token.raw === '=') || (tokens[pragma.next]?.value === '(' && !readArguments.has(name))) return refusal('E_PRAGMA', 'Pragma write outside allowlist')
    return { type: 'read', action: 'pragma', sql }
  }
  if (['SELECT', 'EXPLAIN', 'VALUES'].includes(first)) return { type: 'read', action: 'select', sql }
  let kind: Kind
  let target: ReturnType<typeof readName>
  if (['INSERT', 'REPLACE', 'UPDATE', 'DELETE', 'WITH'].includes(first)) {
    const start = first === 'WITH' ? tokens.findIndex(token => token.depth === 0 && ['INSERT', 'REPLACE', 'UPDATE', 'DELETE', 'SELECT'].some(word => keyword(token, word))) : 0
    if (start < 0) return refusal('E_SQL', 'Unsupported WITH statement')
    const verb = tokens[start].value.toUpperCase()
    if (verb === 'SELECT') return { type: 'read', action: 'select', sql }
    let offset = start + 1
    if (keyword(tokens[offset], 'OR')) offset += 2
    if (keyword(tokens[offset], 'INTO') || keyword(tokens[offset], 'FROM')) offset++
    target = readName(tokens, offset)
    kind = { type: 'dml', action: verb.toLowerCase(), sql, table: target?.name }
  } else if (['CREATE', 'ALTER', 'DROP'].includes(first)) {
    let offset = 1
    const unique = keyword(tokens[offset], 'UNIQUE')
    if (unique) offset++
    if (keyword(tokens[offset], 'VIRTUAL')) return refusal('E_TABLE_KIND', 'Virtual tables are not allowed')
    if (keyword(tokens[offset], 'TEMP') || keyword(tokens[offset], 'TEMPORARY')) return refusal('E_TABLE_KIND', 'Temporary tables are not allowed')
    const object = tokens[offset]?.value.toUpperCase()
    offset = skipExists(tokens, offset + 1)
    target = readName(tokens, offset)
    if (!target) return refusal('E_SQL', 'Missing DDL target')
    if (object === 'INDEX') {
      if (unique) return refusal('E_UNIQUE_INDEX', 'Unique local indexes change shared verdicts')
      if (tokens.some(token => keyword(token, 'USING'))) return refusal('E_INDEX_KIND', 'FTS and embedding indexes are not allowed')
      const on = tokens.findIndex(token => keyword(token, 'ON'))
      const indexed = first === 'DROP' ? undefined : readName(tokens, on + 1)
      const row = context.schemaRows?.find(row => row.type === 'index' && fold(row.name) === fold(target?.name ?? ''))
      kind = { type: 'index', action: first.toLowerCase(), sql, name: target.name, table: indexed?.name ?? row?.tbl_name }
      if (indexed?.database && fold(indexed.database) !== 'main') return refusal('E_MIXED_DATABASES', 'Index in another database')
      if (context.enumArrayColumns?.some(column => mentions(indexExpression(sql), column))) return refusal('E_ENUM_ARRAY_INDEX', 'Enum array columns cannot be indexed')
    } else if (object === 'TYPE' || object === 'DOMAIN') {
      kind = { type: 'type', action: first.toLowerCase(), sql, name: target.name }
    } else if (object === 'TABLE') {
      kind = { type: 'table', action: first.toLowerCase(), sql, table: target.name, pageCoupled: first !== 'ALTER' }
      if (tokens.some((token, i) => keyword(token, 'WITHOUT') && keyword(tokens[i + 1], 'ROWID'))) return refusal('E_TABLE_KIND', 'WITHOUT ROWID is not supported')
      if (first === 'ALTER') {
        const at = target.next
        if (keyword(tokens[at], 'RENAME')) {
          if (keyword(tokens[at + 1], 'TO')) { kind.action = 'rename'; kind.newName = tokens[at + 2]?.value; kind.pageCoupled = true }
          else {
            const col = keyword(tokens[at + 1], 'COLUMN') ? at + 2 : at + 1
            kind.action = 'rename-column'; kind.column = tokens[col]?.value
            if (!keyword(tokens[col + 1], 'TO')) return refusal('E_SQL', 'Expected RENAME COLUMN TO')
            kind.newName = tokens[col + 2]?.value
          }
        } else if (keyword(tokens[at], 'ADD')) {
          const col = keyword(tokens[at + 1], 'COLUMN') ? at + 2 : at + 1
          kind.action = 'add-column'; kind.column = tokens[col]?.value
          kind.declaration = sql.slice(tokens[col + 1]?.start ?? sql.length)
        } else if (keyword(tokens[at], 'DROP')) {
          const col = keyword(tokens[at + 1], 'COLUMN') ? at + 2 : at + 1
          kind.action = 'drop-column'; kind.column = tokens[col]?.value; kind.rebuild = true
        } else if (keyword(tokens[at], 'ALTER') && keyword(tokens[at + 1], 'COLUMN')) {
          kind.column = tokens[at + 2]?.value
          if (!keyword(tokens[at + 3], 'TO') || !identifier(tokens[at + 4])) return refusal('E_SQL', 'Expected ALTER COLUMN c TO name declaration')
          kind.newName = tokens[at + 4].value
          const using = tokens.findIndex((token, i) => i > at + 4 && token.depth === 0 && keyword(token, 'USING'))
          kind.action = using < 0 ? 'alter-column' : 'using'
          kind.declaration = sql.slice(tokens[at + 5]?.start ?? sql.length, using < 0 ? tokens.at(-1)?.end : tokens[using].start).trim()
          if (using >= 0) {
            kind.expression = sql.slice(tokens[using].end, tokens.at(-1)?.end).trim()
            kind.rebuild = true
            if (!kind.expression || !deterministicExpression(kind.expression)) return refusal('E_NONDETERMINISTIC', 'USING must call only deterministic allowlisted functions')
          }
        } else return refusal('E_SQL', 'Unsupported ALTER TABLE')
      }
    } else return refusal('E_SQL', `Unsupported DDL ${object}`)
  } else return refusal('E_SQL', 'Unsupported statement')
  if (!target) return refusal('E_SQL', 'Missing write target')
  if (target.database && fold(target.database) !== 'main') return refusal('E_MIXED_DATABASES', 'Write in another database')
  for (const name of [kind.table, kind.name, ...(kind.action === 'rename' ? [kind.newName] : [])]) {
    if (name && reserved(name, context) && !(kind.type === 'dml' && fold(name) === 'pages' && context.pageId && context.pageRowsOnly)) return refusal('E_APP_TABLE', `Protected object ${name}`)
  }
  // INV-id: custom row identity survives every column-schema operation (U6).
  if (kind.column && fold(kind.column) === 'id' && ['drop-column', 'rename-column', 'alter-column', 'using'].includes(kind.action)) return refusal('E_ID_COLUMN', 'The id column cannot be altered, dropped or renamed')
  if (kind.action === 'rename') {
    const referencedByOther = context.schemaRows?.some(row => row.type === 'table' && fold(row.name) !== fold(kind.table ?? '') && row.sql &&
      tokenize(row.sql).some((token, i, tokens) => keyword(token, 'REFERENCES') && fold(tokens[i + 1]?.value ?? '') === fold(kind.table ?? ''))) ?? context.referenced
    if (referencedByOther) return refusal('E_REFERENCED_TABLE', 'Another visible table references this table')
  }
  // Referenced protected tables could cascade into app data; reads remain unrestricted.
  for (const [i, token] of tokens.entries()) {
    if (keyword(token, 'REFERENCES') && reserved(tokens[i + 1]?.value ?? '', context)) return refusal('E_APP_TABLE', 'Relation targets an app table')
  }
  if (kind.type === 'type' && (context.typeTables?.length ?? 0) > 1) return refusal('E_MIXED_DATABASES', 'Type affects more than one database')
  if (kind.table && context.transactionTables?.some(table => fold(table) !== fold(kind.table ?? ''))) return refusal('E_MIXED_DATABASES', 'One custom database per transaction')
  if (kind.pageCoupled && context.mixedPageDdl) return refusal('E_MIXED_PAGE_DDL', 'Page-coupled DDL cannot mix with other work')
  if (kind.action === 'using' && context.columns?.some(column => column.hidden !== 0 && fold(column.name) === fold(kind.column ?? ''))) return refusal('E_DEPENDENT_OBJECT', 'USING cannot rewrite a generated column')
  if ((kind.action === 'drop-column' || kind.action === 'using') && kind.column) {
    if (context.ftsColumns?.some(column => fold(column) === fold(kind.column ?? ''))) return refusal('E_FTS_COLUMN', 'Column covered by FTS or embedding index')
    const table = context.schemaRows?.find(row => row.type === 'table' && fold(row.name) === fold(kind.table ?? ''))
    if (table?.sql) {
      const dependent = tableDefinition(table.sql).parts.some(part => {
        if (tableConstraint(part)) return mentions(part, kind.column ?? '') && !ownForeignKey(part, kind.column ?? '')
        const parts = tokenize(part)
        const own = fold(parts[0]?.value ?? '') === fold(kind.column ?? '')
        return !own && (parts.some(token => keyword(token, 'GENERATED') || keyword(token, 'AS')) || parts.some(token => keyword(token, 'CHECK') || keyword(token, 'REFERENCES'))) && mentions(part, kind.column ?? '')
      })
      if (dependent) return refusal('E_DEPENDENT_OBJECT', 'Column referenced by a constraint or generated expression')
    }
    if (context.schemaRows?.some(row => row.sql && ['trigger', 'view'].includes(row.type) && (fold(row.tbl_name) === fold(kind.table ?? '') || mentions(row.sql, kind.table ?? '')) && mentions(row.sql, kind.column ?? ''))) return refusal('E_DEPENDENT_OBJECT', 'Column referenced by trigger or view')
  }
  if (kind.rebuild && context.foreignKeysEnabled && context.referenced) return refusal('E_REBUILD_FK_ENABLED', 'Rebuild would fire foreign key actions')
  // Local indexes are removed by the index rule before table expansion; they
  // cannot cause a replicated CREATE/RENAME verdict (I9).
  const schemaNames = context.schemaRows?.filter(row => !(['table', 'type'].includes(kind.type) && row.type === 'index' && row.sql !== null)).map(row => row.name) ?? []
  const columnNames = context.columns?.map(row => row.name) ?? []
  let invalid: Refusal | undefined
  if (kind.action === 'create') invalid = nameRefusal(kind.name ?? kind.table ?? '', schemaNames)
  if (kind.action === 'rename') invalid = nameRefusal(kind.newName ?? '', schemaNames, kind.table)
  if (kind.action === 'add-column') invalid = nameRefusal(kind.column ?? '', columnNames)
  if (['rename-column', 'alter-column', 'using'].includes(kind.action)) invalid = nameRefusal(kind.newName ?? '', columnNames, kind.column)
  if (invalid) return invalid
  if (kind.type === 'table' && kind.action === 'create') {
    let parts: string[]
    try { parts = tableDefinition(sql).parts.filter(part => !tableConstraint(part)) }
    catch (error) { return refusal('E_SQL', String(error)) }
    const names: string[] = []
    for (const part of parts) {
      const name = tokenize(part)[0]?.value ?? ''
      const error = nameRefusal(name, names)
      if (error) return error
      names.push(name)
    }
    if (context.pageId) {
      const definition = tableDefinition(sql)
      const columns = definition.parts.map(part => tokenize(part))
      const id = columns[0]
      // INV-coupled-create: canonical schema has no replica-dependent failure
      // from properties/types/defaults/checks (driver boundary step 1; I11).
      const canonical = columns.length === 1 && id?.length === 4 && id[0].value === 'id' &&
        keyword(id[1], 'TEXT') && keyword(id[2], 'PRIMARY') && keyword(id[3], 'KEY') &&
        tokenize(definition.suffix).length === 1 && keyword(tokenize(definition.suffix)[0], 'STRICT')
      if (!canonical) return refusal('E_MIXED_PAGE_DDL', 'Coupled CREATE requires (id TEXT PRIMARY KEY) STRICT')
    }
  }
  return kind
}
